<template>
  <div class="ta-my-condition-report-requests">
    <PageHeader :title="$t('My Condition Report Requests')"></PageHeader>
    <div class="d-flex justify-content-start">
      <div class="ta-condition-report-requests-list">
        <div class="ta-condition-report-requests-list-header">
          <div class="ta-per-page-wrapper">
            {{ $t("Show") }}
            <b-form-select
              class="mx-2"
              v-model="limit"
              @change="updatePagination"
              :options="perPageOptions"
            ></b-form-select>
            {{ $t("entries") }}
          </div>
        </div>
        <div v-if="conditionReportRequests.loading" class="ta-condition-report-requests-loader">
          <b-spinner label="Spinning"></b-spinner>
          <span class="mt-2">{{ $t("Loading...") }}</span>
        </div>
        <template v-if="!conditionReportRequests.loading">
          <table class="table table-sm">
            <thead>
            <tr>
              <th scope="col">
                {{ $t("Thumbnail") }}
              </th>
              <th scope="col">
                {{ $t("Title") }}
              </th>
              <th scope="col">
                {{ $t("Sale No") }}
              </th>
              <th scope="col">
                {{ $t("Lot No") }}
              </th>
              <th scope="col">
                {{ $t("Created at") }}
              </th>
              <th scope="col">
                {{ $t("Status") }}
              </th>
              <th scope="col">
                {{ $t("Actions") }}
              </th>
            </tr>
            </thead>
            <tbody v-if="conditionReportRequests.results.length">
            <tr v-for="request in conditionReportRequests.results" :key="request.id">
              <td>
                <b-img
                  width="100"
                  :src="request.lot_images.length > 0 && request.lot_images[0].length !== 0
                      ? request.lot_images[0].thumb.url
                      : 'https://cdn.artisio.co/no_image.jpg'"
                  :alt="truncateMessage(request.message)"
                  fluid
                />
              </td>
              <td>
                {{ truncateMessage(request.message) }}
              </td>
              <td>
                {{ request.sale_no }}
              </td>
              <td>
                {{ request.lot_no }}
              </td>
              <td>
                {{ request.created_at | datetime }}
              </td>
              <td>
                    <span
                      class="badge ta-uc-first"
                      :class="{
                        'badge-warning': request.status === 'pending',
                        'badge-danger': request.status === 'rejected',
                        'badge-success': request.status === 'sent',
                      }"
                    >
                      {{ statusLabels[request.status] }}
                    </span>
              </td>
              <td class="col-actions text-nowrap">
                <button
                  class="btn btn-sm btn-secondary btn-pdf-view mr-2"
                  @click="view(request.condition_report)"
                  v-if="request.condition_report"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" style="width: 16px; display: inline-block" fill="none"
                       viewBox="0 0 24 24"
                       stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                  <span class="ta-button-text">{{ $t("View") }}</span>
                </button>
              </td>
            </tr>
            </tbody>
            <tbody v-else>
            <tr>
              <td colspan="15">
                <p class="text-muted text-center py-3">
                  {{ $t(`You don't have data`) }}
                </p>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="ta-condition-report-requests-list-footer">
            <div>
              {{
                $t("Showing {start} to {finish} of {total} entries", {
                  start: (conditionReportRequests.pagination.page - 1) * conditionReportRequests.pagination.limit + 1,
                  finish: conditionReportRequests.pagination.page * conditionReportRequests.pagination.limit > conditionReportRequests.totalRows
                    ? conditionReportRequests.totalRows
                    : conditionReportRequests.pagination.page * conditionReportRequests.pagination.limit,
                  total: conditionReportRequests.totalRows,
                })
              }}
            </div>
            <b-pagination
              v-model="page"
              :total-rows="conditionReportRequests.totalRows"
              :per-page="conditionReportRequests.pagination.limit"
              @input="updatePagination"
              class="ta-condition-report-requests-list-pagination"
            ></b-pagination>
          </div>
        </template>
      </div>
    </div>
    <ConditionReportModal
      v-if="modal.condition_report"
      :report="modal.condition_report"
      v-model="modal.show"
    />
  </div>
</template>

<script>
import {mapState, mapGetters, mapActions} from "vuex";
import {
  CONDITION_REPORT_REQUESTS_PAGINATION_PAGE,
  CONDITION_REPORT_REQUESTS_PAGINATION_LIMIT,
} from "../constants";
import logger from "../logger";
import PageHeader from "@/components/PageHeader";
import ConditionReportModal from "@/components/condition-report/ConditionReportModal.vue";

export default {
  name: "MyConditionReportRequests",
  components: {ConditionReportModal, PageHeader},
  data: () => {
    return {
      limit: CONDITION_REPORT_REQUESTS_PAGINATION_LIMIT,
      page: CONDITION_REPORT_REQUESTS_PAGINATION_PAGE,
      modal: {
        show: false,
        condition_report: null,
      },
      statusLabels: {
        pending: "Pending",
        rejected: "Rejected",
        sent: "Received",
      },
    };
  },

  computed: {
    ...mapState(["perPageOptions", "conditionReportRequests"]),
    ...mapGetters(["currentLocale"]),
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler(newVal) {
        if (newVal.page) this.page = newVal.page;
        if (newVal.limit) this.limit = newVal.limit;
        this.getUpdatedConditionReportRequests();
      },
    },
  },
  methods: {
    ...mapActions(["getInvoicePdf", "requestPaymentUrl", "getConditionReportRequests"]),
    updatePagination() {
      this.$router
        .push({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            page: this.page,
            limit: this.limit,
          },
        })
        .catch((err) => {
          logger.error("Failed to update route for condition report requests", err);
        });
    },
    getUpdatedConditionReportRequests() {
      this.getConditionReportRequests({
        pagination: {
          page: this.page,
          limit: this.limit
        },
      });
    },
    async view(condition_report) {
      this.modal.condition_report = condition_report;
      this.modal.show = true;
    },
    truncateMessage(message) {
      const maxLength = 20;
      return message.length > maxLength ? message.split("").slice(0, maxLength).join("") + "..." : message;
    },
  },
};
</script>

<style scoped lang="scss">
.ta-condition-report-requests-list-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.ta-condition-report-requests-loader {
  height: 300px;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.ta-per-page-wrapper {
  display: flex;
  align-items: center;
}

.ta-condition-report-requests-list {
  flex: 1;
}

.ta-condition-report-requests-list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ta-uc-first {
  text-transform: capitalize;
}

.pagination.ta-condition-report-requests-list-pagination {
  margin-bottom: 0 !important;
}
</style>
